"use client";

import React, { useEffect, useState } from "react";
import { useFormik, type FormikErrors } from "formik";
import { LiaRupeeSignSolid } from "react-icons/lia";
import estimateTaxPercentage from "@/app/onboarding/_components/tax-estimator";
import { clientLogger } from "@/lib/logger";
import {
	formatToIndianCurrency,
	isValidSalary,
	unformatIndianCurrency,
} from "@/app/product/utils/currency.utils";
import { fetchUserData } from "@/app/product/utils/user.utils";
import { Logo } from "@/design-elements/logo";
import { trackEvent, GTMEvent } from "@/lib/utils/gtm-events";
import { useAppData } from "@/store/appStore";
import { guestAssetConfig } from "@/app/product/_actions/guest.assetConfig";
import { getSlabsByRegime, findSlabByValue } from "@/constants/taxSlabs";
import TaxSlabDropdown from "@/components/taxation/TaxSlabDropdown";

interface StepSalaryProps {
	onSubmit: (salary: number) => Promise<void>;
	onSkip: () => void;
	initialSalary?: number;
}

interface FormValues {
	salary: string;
}

const SALARY_COPY = {
	heroTitle: "What's your annual salary?",
	heroSubtitle:
		"We know it's personal. We use it only to show your closest possible savings—never shared with anyone.",
	label: "Annual CTC",
	placeholder: "12,00,000",
	helperText: "Enter your annual CTC in INR to continue.",
	submit: "Next",
	submitting: "Submitting...",
	skip: "Skip",
	errorSubmit: "Failed to submit salary. Please try again.",
	logSubmit: "Onboarding: Salary submitted",
	logSkip: "Onboarding: Salary step skipped",
	taxMessage: (percentage: number) =>
		`You can save ${percentage.toFixed(0)}% on cars and devices.`,
	slabHeroTitle: "Which income tax slab applies to you?",
	slabHeroSubtitle: "We'll use this to calculate your exact savings",
	slabLabel: "Tax bracket",
};

const StepSalary: React.FC<StepSalaryProps> = ({
	onSubmit,
	onSkip,
	initialSalary,
}) => {
	const formattedInitialSalary =
		typeof initialSalary === "number"
			? formatToIndianCurrency(initialSalary.toString())
			: "";

	const [taxPercentage, setTaxPercentage] = useState<number | null>(null);
	const [user, setUser] = useState<User | undefined>(undefined);
	const [appData, setData] = useAppData();

	const showSlabSelector = !!appData?.restrictDeviceCtcView;

	const [slabRegime, setSlabRegime] = useState<"new" | "old">(
		appData?.taxRegime ?? "new",
	);

	const [selectedSlabValue, setSelectedSlabValue] = useState<string>(() => {
		const regime = appData?.taxRegime ?? "new";
		if (initialSalary) {
			const match = findSlabByValue(initialSalary, regime);
			if (match) return match.value.toString();
		}
		return (3000000).toString();
	});

	const [isSlabSubmitting, setIsSlabSubmitting] = useState(false);

	useEffect(() => {
		fetchUserData({ setUser });
	}, []);

	const handleSlabSubmit = async () => {
		if (!selectedSlabValue) return;
		setIsSlabSubmitting(true);
		try {
			const salary = parseInt(selectedSlabValue, 10);
			trackEvent(GTMEvent.CTC_UPDATED, {
				ctc: salary,
				page_path: "Onboarding page",
			});
			await onSubmit(salary);
			if (!appData.authorization) {
				guestAssetConfig({
					salary: selectedSlabValue,
					taxRegime: slabRegime,
					variantId: appData.carVariantId || null,
					cityId: appData.cityId || null,
					selectedTenure: appData.selectedCarTenure
						? appData.selectedCarTenure.toString()
						: null,
				}).catch((err) => {
					clientLogger.error(
						"guestAssetConfig failed in step-salary slab",
						err as Error,
					);
				});
			}
			clientLogger.info(SALARY_COPY.logSubmit, {
				loggedIn: !!user,
				salary: true,
				userId: user ? user.id : null,
			});
		} catch (err) {
			clientLogger.error("Error submitting slab salary:", err as Error);
		} finally {
			setIsSlabSubmitting(false);
		}
	};

	const handleSkip = () => {
		formik.resetForm();
		trackEvent(GTMEvent.CTC_SKIPPED);
		onSkip();
		clientLogger.info(SALARY_COPY.logSkip, {
			loggedIn: !!user,
			salary: false,
			userId: user ? user.id : null,
		});
	};

	const formik = useFormik<FormValues>({
		initialValues: { salary: formattedInitialSalary },
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			const unformatted = unformatIndianCurrency(values.salary || "");
			const validation = isValidSalary(unformatted);
			if (!validation.valid) {
				errors.salary = validation.message;
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting, setFieldError }) => {
			const unformattedSalary = unformatIndianCurrency(values.salary || "");
			try {
				trackEvent(GTMEvent.CTC_UPDATED, {
					ctc: parseFloat(unformattedSalary),
					page_path: "Onboarding page",
				});
				await onSubmit(parseFloat(unformattedSalary));
				if (!appData.authorization) {
					guestAssetConfig({
						salary: unformattedSalary,
						taxRegime: appData.taxRegime,
						variantId: appData.carVariantId || null,
						cityId: appData.cityId || null,
						selectedTenure: appData.selectedCarTenure
							? appData.selectedCarTenure.toString()
							: null,
					}).catch((err) => {
						clientLogger.error(
							"guestAssetConfig failed in step-salary",
							err as Error,
						);
					});
				}
				clientLogger.info(SALARY_COPY.logSubmit, {
					loggedIn: !!user,
					salary: true,
					userId: user ? user.id : null,
				});
			} catch (err) {
				clientLogger.error("Error submitting salary:", err as Error);
				setFieldError("salary", SALARY_COPY.errorSubmit);
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSalaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = event.target.value;
		const unformattedValue = unformatIndianCurrency(inputValue);
		if (/^\d*\.?\d*$/.test(unformattedValue) || unformattedValue === "") {
			const formattedValue = formatToIndianCurrency(unformattedValue);
			formik.setFieldValue("salary", formattedValue, true);
			if (!formik.touched.salary) {
				formik.setFieldTouched("salary", true, false);
			}
		}
	};

	useEffect(() => {
		if (showSlabSelector) return;
		const unformatted = unformatIndianCurrency(formik.values.salary || "");
		const validation = isValidSalary(unformatted);
		if (validation.valid) {
			const num = parseFloat(unformatted);
			if (!Number.isNaN(num) && num > 0) {
				setTaxPercentage(estimateTaxPercentage(num));
				return;
			}
		}
		setTaxPercentage(null);
	}, [formik.values.salary, showSlabSelector]);

	const salaryError =
		formik.touched.salary && formik.errors.salary
			? formik.errors.salary
			: undefined;

	const isSalaryValid = isValidSalary(
		unformatIndianCurrency(formik.values.salary || ""),
	).valid;

	const submitDisabled =
		formik.isSubmitting || !isSalaryValid || !formik.values.salary;
	const showSubmitDisabledStyle = submitDisabled && !formik.isSubmitting;

	if (showSlabSelector) {
		const slabSubmitDisabled = !selectedSlabValue || isSlabSubmitting;
		const showSlabDisabledStyle = slabSubmitDisabled && !isSlabSubmitting;
		return (
			<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
				<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
					<div className="mx-auto">
						<Logo
							showText={true}
							invert
							size={40}
							className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
						/>
					</div>
					<div className="flex flex-col md:flex-1 md:justify-center">
						<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
							<h1 className="text-[26px] md:text-[28px] leading-tight">
								{SALARY_COPY.slabHeroTitle}
							</h1>
							<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3">
								{SALARY_COPY.slabHeroSubtitle}
							</p>
						</div>
						<div className="mt-6">
							<p className="text-sm text-[var(--automint-black)] md:text-white mb-2">
								{SALARY_COPY.slabLabel}
							</p>
							<TaxSlabDropdown
								value={selectedSlabValue}
								regime={slabRegime}
								onChange={setSelectedSlabValue}
								onRegimeChange={(r) => {
									setSlabRegime(r);
									setData({ taxRegime: r });
									setSelectedSlabValue(getSlabsByRegime(r)[0].value.toString());
								}}
							/>
						</div>
						<div className="mt-5">
							<button
								id="onboarding_tax_slab_submit"
								type="button"
								onClick={handleSlabSubmit}
								disabled={slabSubmitDisabled}
								className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSlabDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
							>
								{isSlabSubmitting ? SALARY_COPY.submitting : SALARY_COPY.submit}
							</button>
						</div>
						<div className="mt-4 mb-2 text-center">
							<button
								id="skip_tax_slab"
								type="button"
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleSkip}
								className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
							>
								{SALARY_COPY.skip}
							</button>
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px]  leading-tight">
							{SALARY_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3 whitespace-pre-line">
							{SALARY_COPY.heroSubtitle}
						</p>
					</div>

					<div className="mt-6">
						<label
							htmlFor="onboarding_salary_input"
							className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
						>
							{SALARY_COPY.label}
						</label>
						<div className="relative">
							<LiaRupeeSignSolid
								className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]"
								size={20}
							/>
							<input
								id="onboarding_salary_input"
								name="salary"
								type="text"
								inputMode="decimal"
								autoFocus
								placeholder={SALARY_COPY.placeholder}
								value={formik.values.salary}
								onChange={handleSalaryChange}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !submitDisabled) {
										formik.handleSubmit();
									}
								}}
								className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] pl-11 pr-4 text-[15px] focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)] disabled:opacity-60"
							/>
						</div>
						{salaryError ? (
							<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-2">
								{salaryError}
							</p>
						) : taxPercentage !== null ? (
							<p className="text-[var(--automint-black)] md:text-[var(--colors-automint-neon-500)] text-xs mt-2">
								{SALARY_COPY.taxMessage(taxPercentage)}
							</p>
						) : null}
					</div>

					<div className="mt-5">
						<button
							id="onboarding_salary_submit"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={submitDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSubmitDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting
								? SALARY_COPY.submitting
								: SALARY_COPY.submit}
						</button>
					</div>

					<div className="mt-4 mb-2 text-center">
						<button
							id="skip_salary"
							type="button"
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleSkip}
							className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
						>
							{SALARY_COPY.skip}
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

export default StepSalary;
